"use client"

import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  Updater,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import { Button } from "@repo/ui/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu"
import { Input } from "@repo/ui/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/ui/table"
import { cn } from "@repo/ui/lib/utils"
import { AnimatePresence, motion } from "framer-motion"
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Edit,
  Eye,
  FilePlus,
  RefreshCw,
  Search,
  Trash2,
  X
} from "lucide-react"
import { useEffect, useState } from "react"

interface PageWindowOptions {
  currentPage: number
  pageCount: number
  windowSize?: number
}

type PageWindowItem = number | "ellipsis"

function getPageWindow({
  currentPage,
  pageCount,
  windowSize = 5,
}: PageWindowOptions): PageWindowItem[] {
  if (pageCount <= 0) {
    return []
  }
  const safeWindowSize = Math.max(3, windowSize)
  const clampedCurrentPage = Math.min(Math.max(currentPage, 1), pageCount)

  if (pageCount <= safeWindowSize) {
    return Array.from({ length: pageCount }, (_, index) => index + 1)
  }

  const hasLeftEllipsis = clampedCurrentPage > 3
  const hasRightEllipsis = clampedCurrentPage < pageCount - 2

  if (!hasLeftEllipsis && hasRightEllipsis) {
    return [1, 2, 3, 4, "ellipsis", pageCount]
  }

  if (hasLeftEllipsis && !hasRightEllipsis) {
    return [1, "ellipsis", pageCount - 3, pageCount - 2, pageCount - 1, pageCount]
  }

  return [1, "ellipsis", clampedCurrentPage - 1, clampedCurrentPage, clampedCurrentPage + 1, "ellipsis", pageCount]
}
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  searchPlaceholder?: string
  onAdd?: () => void
  onEdit?: (row: TData) => void
  onDelete?: (row: TData) => void
  onView?: (row: TData) => void
  onRowDoubleClick?: (row: TData) => void
  addButtonLabel?: string
  isLoading?: boolean
  error?: string
  // Nouvelles props pour la pagination serveur
  totalCount?: number
  pageCount?: number
  currentPage?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  serverSidePagination?: boolean,
  renderSearchOutside?: boolean
  externalGlobalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  enableRefresh?: boolean
  // Selection control
  onSelectionChange?: (selectedRows: TData[]) => void
  enableRowSelection?: boolean
  resetSelectionKey?: number | string
  // Sticky last column
  stickyLastColumn?: boolean
  // Custom empty state (overrides the default "Aucun résultat" block)
  emptyState?: React.ReactNode
  /** Column ids (accessorKey / id) sorted server-side; default: espece */
  serverSortedColumnIds?: string[]
  /** When set, primary sort on a server column triggers refetch; other columns clear server sort */
  onServerSideSortingChange?: (
    sort: { columnId: string; direction: 'asc' | 'desc' } | null
  ) => void
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = "Rechercher...",
  onAdd,
  onEdit,
  onDelete,
  onView,
  onRowDoubleClick,
  addButtonLabel = "Ajouter",
  isLoading = false,
  error,
  totalCount = 0,
  pageCount = 1,
  currentPage = 1,
  pageSize: externalPageSize,
  onPageChange,
  onPageSizeChange,
  serverSidePagination = false,
  renderSearchOutside = false,
  externalGlobalFilter,
  onGlobalFilterChange,
  enableRefresh = false,
  onSelectionChange,
  enableRowSelection = true,
  resetSelectionKey,
  stickyLastColumn = false,
  emptyState,
  serverSortedColumnIds,
  onServerSideSortingChange,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState({})

  // Reset selection when resetSelectionKey changes
  useEffect(() => {
    if (resetSelectionKey !== undefined) {
      console.log("resetSelectionKey", resetSelectionKey)
      setRowSelection({})
    }
  }, [resetSelectionKey])
  const [pageSize, setPageSize] = useState(externalPageSize || 10)
  const [clientPageIndex, setClientPageIndex] = useState(0)

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("")
  const globalFilter = externalGlobalFilter !== undefined ? externalGlobalFilter : internalGlobalFilter;
  const setGlobalFilter = (value: string) => {
    if (onGlobalFilterChange && externalGlobalFilter !== undefined) {
      onGlobalFilterChange(value);
    } else {
      setInternalGlobalFilter(value);
    }
  };

  const safeServerPageCount = serverSidePagination ? Math.max(pageCount, 1) : 0
  const safeServerCurrentPage = serverSidePagination
    ? Math.min(Math.max(currentPage, 1), safeServerPageCount)
    : 1

  const serverSortColumnIdsResolved = serverSortedColumnIds ?? ['espece']
  const primarySort = sorting[0]
  const manualSorting = Boolean(
    onServerSideSortingChange &&
    primarySort &&
    serverSortColumnIdsResolved.includes(String(primarySort.id))
  )

  function handleSortingChange(updater: Updater<SortingState>): void {
    if (!onServerSideSortingChange) {
      setSorting(updater)
      return
    }
    setSorting((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (next.length === 0) {
        onServerSideSortingChange(null)
      } else {
        const primary = next[0]
        if (serverSortColumnIdsResolved.includes(String(primary?.id))) {
          onServerSideSortingChange({
            columnId: String(primary?.id),
            direction: primary?.desc ? 'desc' : 'asc',
          })
        } else {
          onServerSideSortingChange(null)
        }
      }
      return next
    })
  }

  function handleClientPaginationChange(updater: Updater<PaginationState>): void {
    const current: PaginationState = { pageIndex: clientPageIndex, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    setClientPageIndex(next.pageIndex)
    setPageSize(next.pageSize)
  }

  const pagination: PaginationState = serverSidePagination
    ? { pageIndex: safeServerCurrentPage - 1, pageSize }
    : { pageIndex: clientPageIndex, pageSize }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Utiliser la pagination côté serveur uniquement si serverSidePagination est true
    ...(serverSidePagination
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: serverSidePagination ? undefined : handleClientPaginationChange,
    // Pagination manuelle si serveur
    manualPagination: serverSidePagination,
    manualSorting,
    pageCount: serverSidePagination ? pageCount : undefined,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      globalFilter,
      pagination,
    },
  })

  // Expose selection upstream
  useEffect(() => {
    if (!onSelectionChange) return;
    const selected = table.getSelectedRowModel().rows.map(r => r.original as TData)
    onSelectionChange(selected)
  }, [rowSelection, table, onSelectionChange])

  // Gérer les changements de taille de page
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    if (serverSidePagination) {
      if (onPageSizeChange) {
        onPageSizeChange(newPageSize)
      }
      if (onPageChange) {
        onPageChange(1)
      }
    } else {
      setPageSize(newPageSize)
      setClientPageIndex(0)
    }
  }

  // Fonction pour simuler un rafraîchissement des données
  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => {
      setIsRefreshing(false)
    }, 800)
  }
  const SearchInput = () => (
    <div className="relative w-full sm:w-96">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={searchPlaceholder}
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        className="pl-9 bg-background/60 backdrop-blur-sm border-border focus:border-primary transition-all"
      />
      {globalFilter && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => setGlobalFilter("")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Barre de recherche */}
        {searchKey && !renderSearchOutside && <SearchInput />}
      </div>

      {/* Message d'erreur */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-4 flex items-center gap-2"
          >
            <X className="h-5 w-5 text-destructive" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div>
        {/* Table Container avec état de chargement */}
        <div className="relative">
          {/* Overlay de chargement */}
          <AnimatePresence>
            {(isLoading || isRefreshing) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-background/60 backdrop-blur-sm z-10 flex items-center justify-center"
              >
                <div className="flex flex-col items-center gap-3">
                  <RefreshCw className="h-8 w-8 text-primary animate-spin" />
                  <span className="text-primary font-medium">Chargement des données...</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className={cn(
            "overflow-x-auto",
            stickyLastColumn && "max-w-full"
          )}>
            <Table className={cn(
              "border-collapse",
              stickyLastColumn && "min-w-full"
            )}>
              <TableHeader className="bg-background">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="border-b border-border hover:bg-transparent">
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="py-3.5 px-4 text-foreground font-semibold text-left"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                    {onView || onEdit || onDelete ? (
                      <TableHead
                        id="actions"
                        className={cn(
                          "w-[100px]",
                          stickyLastColumn && "sticky right-0 bg-background z-20 shadow-[inset_-1px_0_0_0_hsl(var(--border))]"
                        )}
                      >
                        Actions
                      </TableHead>
                    ) : null}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row, i) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={cn(
                        "border-b border-border transition-colors hover:bg-muted/50 cursor-pointer",
                        i % 2 === 0 ? "bg-background" : "bg-muted/20",
                        row.getIsSelected() && "bg-primary/10"
                      )}
                      onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3.5 px-4">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                      <TableCell
                        className={cn(
                          "py-2 px-4",
                          stickyLastColumn && cn(
                            "sticky right-0 z-10 shadow-[inset_-1px_0_0_0_hsl(var(--border))]",
                            i % 2 === 0 ? "bg-background" : "bg-muted/20",
                            row.getIsSelected() && "bg-primary/10"
                          )
                        )}
                      >
                        <div className="flex items-center justify-end gap-2 bg-background">
                          {onView && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onView(row.original)}
                              className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          {onEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => onEdit(row.original)}
                              className="h-8 w-8 text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {onDelete && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-popover border border-border shadow-lg rounded-lg p-2">
                                <div className="px-3 py-2 text-sm text-muted-foreground font-medium">
                                  Confirmer la suppression ?
                                </div>
                                <DropdownMenuItem
                                  className="flex items-center gap-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10 cursor-pointer"
                                  onClick={() => onDelete(row.original)}
                                >
                                  <Check className="h-4 w-4" />
                                  <span>Oui, supprimer</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      className="h-32 text-center text-muted-foreground"
                    >
                      {isLoading ? (
                        <div className="flex flex-col items-center gap-3">
                          <RefreshCw className="h-8 w-8 text-primary animate-spin" />
                          <span>Chargement des données...</span>
                        </div>
                      ) : emptyState ? (
                        emptyState
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <FilePlus className="h-8 w-8 text-muted-foreground" />
                          <p>Aucun résultat trouvé.</p>
                          {onAdd && (
                            <Button
                              variant="link"
                              onClick={onAdd}
                              className="text-primary hover:text-primary/80"
                            >
                              {addButtonLabel}
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination améliorée */}
        <div className="flex items-center justify-between px-4 py-4 bg-muted/30 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Afficher</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => handlePageSizeChange(Number(value))}
              >
                <SelectTrigger className="w-16 h-8 border-border focus:ring-primary bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>lignes</span>
            </div>

            <span className="hidden sm:inline-flex ml-2">
              |
            </span>

            <span className="hidden sm:inline-flex">
              {serverSidePagination
                ? `${totalCount} résultats`
                : `${table.getFilteredRowModel().rows.length} résultats`}
            </span>
          </div>

          {(() => {
            const clientPageIndex = table.getState().pagination?.pageIndex ?? 0
            const clientPageCount = table.getPageCount()
            const effectivePageCount = serverSidePagination ? pageCount : clientPageCount
            const currentPageNumber = serverSidePagination
              ? safeServerCurrentPage
              : clientPageCount > 0
                ? clientPageIndex + 1
                : 1
            const pages = getPageWindow({
              currentPage: currentPageNumber,
              pageCount: effectivePageCount,
              windowSize: 5,
            })
            const isFirstPage = currentPageNumber <= 1 || effectivePageCount <= 1
            const isLastPage =
              effectivePageCount <= 1 || currentPageNumber >= effectivePageCount

            const handlePageChange = (targetPage: number) => {
              if (targetPage < 1) {
                return
              }
              if (serverSidePagination) {
                if (!onPageChange || effectivePageCount === 0) {
                  return
                }
                const clamped = Math.min(targetPage, effectivePageCount)
                onPageChange(clamped)
                return
              }
              if (clientPageCount === 0) {
                return
              }
              const clampedIndex = Math.min(targetPage - 1, clientPageCount - 1)
              if (clampedIndex < 0 || clampedIndex === clientPageIndex) {
                return
              }
              setClientPageIndex(clampedIndex)
            }

            return (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-border"
                  onClick={() => handlePageChange(currentPageNumber - 1)}
                  disabled={isFirstPage}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {pages.map((item, index) => {
                    if (item === "ellipsis") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 text-muted-foreground"
                        >
                          ...
                        </span>
                      )
                    }

                    const pageNumber = item as number
                    const isCurrent = pageNumber === currentPageNumber

                    return (
                      <Button
                        key={pageNumber}
                        variant={isCurrent ? "default" : "outline"}
                        size="icon"
                        className={cn(
                          "h-8 w-8",
                          isCurrent
                            ? "bg-primary hover:bg-primary text-primary-foreground"
                            : "border-border text-foreground"
                        )}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    )
                  })}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-border"
                  onClick={() => handlePageChange(currentPageNumber + 1)}
                  disabled={isLastPage}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}